import { useState, useEffect } from 'react';
import '../styles/Sidebar.css';
import '../styles/SharedAnimations.css';

const ICCIDGenerator = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedOperator, setSelectedOperator] = useState('');
  const [generatedICCID, setGeneratedICCID] = useState('');
  const [countries, setCountries] = useState([]);
  const [operators, setOperators] = useState([]);
  const [operatorsData, setOperatorsData] = useState({});

  useEffect(() => {
    setTimeout(() => setIsVisible(true), 100);
  }, []);

  useEffect(() => {
    fetch('/data/operators.json')
      .then(response => response.json())
      .then(data => setOperatorsData(data));
  }, []);

  useEffect(() => {
    setCountries(Object.keys(operatorsData).sort());
  }, [operatorsData]);

  useEffect(() => {
    if (selectedCountry) {
      setOperators(operatorsData[selectedCountry].operators);
      setSelectedOperator('');
      setGeneratedICCID('');
    }
  }, [selectedCountry, operatorsData]);

  const generateICCID = () => {
    if (!selectedCountry || !selectedOperator) return;

    const operator = operators.find(op => op.name === selectedOperator);
    if (!operator) return;

    const { mcc, mnc, prefix } = operator;
    
    // ICCID structure: 89 + MCC + MNC + N(12 digits) + Luhn check digit
    let iccid = '89' + mcc + mnc;
    
    // Generate 11 random digits (instead of 12) to make it 19 characters total
    for (let i = 0; i < 11; i++) {
      iccid += Math.floor(Math.random() * 10);
    }
    
    // Add Luhn check digit
    const checkDigit = calculateLuhnCheckDigit(iccid);
    iccid += checkDigit;

    setGeneratedICCID(iccid);
  };

  const calculateLuhnCheckDigit = (number) => {
    let sum = 0;
    let isEven = true;
    
    for (let i = number.length - 1; i >= 0; i--) {
      let digit = parseInt(number[i]);
      
      if (isEven) {
        digit *= 2;
        if (digit > 9) {
          digit -= 9;
        }
      }
      
      sum += digit;
      isEven = !isEven;
    }
    
    return ((Math.floor(sum / 10) + 1) * 10 - sum) % 10;
  };

  return (
    <div className={`fade-in-content ${isVisible ? 'visible' : ''} p-6 bg-[#1b263b] text-white`}>
      <h1 className="text-4xl font-bold mb-8 text-blue-500">ICCID Generator</h1>
      
      <div className="space-y-6">
        <div>
          <h2 className="text-xl mb-2">Select Country</h2>
          <select
            value={selectedCountry}
            onChange={(e) => setSelectedCountry(e.target.value)}
            className="w-[400px] p-2 rounded bg-[#1a2332] border border-[#415a77] text-white"
          >
            <option value="">-- Select Country --</option>
            {countries.map(country => (
              <option key={country} value={country}>{country}</option>
            ))}
          </select>
        </div>
<br></br>
        <div>
          <h2 className="text-xl mb-2">Select Mobile Operator</h2>
          <select
            value={selectedOperator}
            onChange={(e) => setSelectedOperator(e.target.value)}
            className="w-[400px] p-2 rounded bg-[#1a2332] border border-[#415a77] text-white"
            disabled={!selectedCountry}
          ><br></br>
            <option value="">-- Select Mobile Operator --</option>
            {operators.map(operator => (
              <option key={operator.name} value={operator.name}>
                {operator.name}
              </option>
            ))}
          </select>
        </div>
         <br></br>
        <button
          onClick={generateICCID}
          disabled={!selectedCountry || !selectedOperator}
          className="login-button w-[200px]"
          width="150px"
        >
          Generate ICCID
        </button>
<br></br>
        {generatedICCID && (
          <div className="mt-6 p-4 bg-[#1a2332] rounded border border-[#415a77]">
            <h3 className="text-lg mb-2">Generated ICCID:</h3>
            <code className="text-xl text-green-400 font-mono">{generatedICCID}</code>
          </div>
        )}
<br></br>
        {selectedCountry && (
          <div className="mt-8">
            <h3 className="text-lg font-semibold mb-4">Available IMSI Codes for {selectedCountry}:</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full border-collapse bg-[#1b263b]">
                <thead>
                  <tr>
                    <th className="p-4 text-left text-xl font-semibold bg-[#1b263b] border-2 border-[#1e2d3d]">Operator</th>
                    <th className="p-4 text-left text-xl font-semibold bg-[#1b263b] border-2 border-[#1e2d3d]">IMSI Code (MCC + MNC)</th>
                    <th className="p-4 text-left text-xl font-semibold bg-[#1b263b] border-2 border-[#1e2d3d]">7-digit Code</th>
                  </tr>
                </thead>
                <tbody>
                  {operatorsData[selectedCountry]?.operators.map((operator, index) => (
                    <tr 
                      key={index} 
                      className={`bg-[#1b263b] ${
                        operator.name === selectedOperator ? 'bg-[rgba(59,130,246,0.1)]' : ''
                      }`}
                    >
                      <td className="p-4 text-lg whitespace-nowrap border-2 border-[#1e2d3d]">{operator.name}</td>
                      <td className="p-4 text-lg whitespace-nowrap font-mono border-2 border-[#1e2d3d]">{operator.mcc + operator.mnc}</td>
                      <td className="p-4 text-lg whitespace-nowrap font-mono border-2 border-[#1e2d3d]">
                        {(() => {
                          const code = operator.mcc + operator.mnc;
                          if (code.length === 5) {
                            return code + '00';
                          } else if (code.length === 6) {
                            return code + '0';
                          } else {
                            return code;
                          }
                        })()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ICCIDGenerator; 